import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  InputLabel,
  Box,
  Button,
  Skeleton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DatePickerMonthEnds from "../../common/DatePicker/DatePickerMonthEnds";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import {
  minDate,
  maxDate,
  getMappedBusinessEntityIDs,
  applyFilterForValues,
} from "../../../utils/utils";
import useStyles from "./STDTD.styles";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import {
  SHORTTERMDEBTTOTALDEBTRATIO,
  STDTDR,
  MEASURE,
  DIMENSION,
} from "../../../utils/constants";
import { sliceObjectByIndex } from "../../../utils/utils";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import STDTDExport from "./STDTDExport";

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (!nestedData || !Array.isArray(nestedData)) {
    return [];
  }

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  return nestedData.map((row) => {
    const { datas, ...fixedProps } = row;
    const formattedDatas = {};

    if (datas) {
      for (const key in datas) {
        if (Object.hasOwnProperty.call(datas, key)) {
          let value = datas[key];

          if (shouldDivide) {
            value = value / divisor;
          }

          if (shouldFormatDecimals) {
            formattedDatas[key] = value.toFixed(decimals);
          } else {
            formattedDatas[key] = value;
          }
        }
      }
    }

    return {
      ...fixedProps,
      ...formattedDatas,
    };
  });
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const STDTDFiltersAndReports = () => {
  const { classes } = useStyles();
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();
  const [isTouched, setIsTouched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    hrGapResultDataLoading,
    hrGapResultDataError,
    hrGapResultData,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    allowedMonthEnds,
    localCurrency,
    setLocalCurrency,
    selectedMonthEnd,
    setSelectedMonthEnd,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    fetchHrGapResultReport,
    setHrGapResultData,
  } = useHedgeRatioContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { regions, email } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    const filteredModelDatabases = (entityObject?.dateModels || []).filter(
      (model) => model?.treasuryKRIGlobalView === true,
    );
    setModelDatabases((prev) => {
      if (
        prev.length === filteredModelDatabases?.length &&
        prev.every(
          (item, index) =>
            item.modelName === filteredModelDatabases[index]?.modelName,
        )
      ) {
        return prev;
      }
      return filteredModelDatabases;
    });
    const { localCurrency: currency } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  useEffect(() => {
    const modelObject = modelDatabases?.find((model) => {
      if (model?.monthEnd) {
        const datePart = model.monthEnd?.slice(0, 10);
        return datePart === selectedMonthEnd?.format("YYYY-MM-DD");
      }
      return false;
    });
    setSelectedModelDatabase(modelObject?.modelName || null);
  }, [modelDatabases, selectedMonthEnd, setSelectedModelDatabase]);

  const handleOpenChange = (open) => {
    if (!open) {
      setIsTouched(true);
    }
    setIsOpen(open);
  };

  const APPROVAL_FLOW_PARAMS = {
    region: null,
    businessEntityID: selectedBusinessEntityID,
    selectedReportDate: selectedMonthEnd,
    isGlobal: true,
    isModelerReport: false,
    isTreasuryKRIReport: true,
    isKRISummaryReport: false,
    isSFCKRIGlobalSummaryReport: false,
    isRegionKRIGlobalSummaryReport: false,
    isTFSCKRIGlobalSummaryReport: false,
    measure: MEASURE.SHORT_TERM_DEBT_TO_TOTAL_DEBT_RATIO,
    dimension: DIMENSION.LIQUIDITY_RISK,
    dimension2: null,
  };

  const handleSubmitBtn = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      fetchHrGapResultReport(STDTDR);
      fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
    } else {
      setHrGapResultData([]);
      setApprovalFlow([]);
      alert(
        "The selected Entity / Month End does not have any Model Database associated (or) it does not have Treasury KRI Global View.",
      );
    }
  };

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => {
        return fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
      })
      .then(() => {
        return sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        });
      });
  };

  const DATA_INDEX = 1;
  const DATA_LIMIT = 12;

  const dynamicColumns =
    hrGapResultData &&
    hrGapResultData?.length > 0 &&
    hrGapResultData?.[0]?.datas
      ? Object.keys(
          sliceObjectByIndex(
            hrGapResultData?.[0]?.datas,
            DATA_INDEX,
            DATA_LIMIT,
          ),
        )?.map((key) => {
          return {
            id: key,
            label: key,
            minWidth: 100,
            align: "right",
          };
        })
      : [];

  const columns = [
    {
      id: "reportingAccountName",
      label: "Short-term Debt to Total Debt Ratio",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const exportColumns = [
    {
      id: "reportingAccountName",
      label: "Short-term Debt to Total Debt Ratio",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const { TOTAL_DEBT, SHORT_TERM_DEBT } = SHORTTERMDEBTTOTALDEBTRATIO || {};

  const shortTermLiabHrGapResultData = hrGapResultData?.filter(
    (item) => item.reportingAccountName === SHORT_TERM_DEBT,
  );

  const preprocessedData = flattenReportData(
    shortTermLiabHrGapResultData,
    selectedFigureDivisor,
    selectedDecimalsIn,
  );

  const preprocessedDataExport = flattenReportData(
    shortTermLiabHrGapResultData,
    null,
    null,
  );

  let selectedMonthTotalDebt = 0;
  let sumOf12MonthShortTermLiabilities = 0;
  let formattedSelectedMonthTotalDebt = 0;
  let formattedSumOf12MonthShortTermLiabilities = 0;

  hrGapResultData.forEach((data) => {
    if (data.reportingAccountName === TOTAL_DEBT) {
      selectedMonthTotalDebt =
        data?.datas[selectedMonthEnd?.format("MM/DD/YYYY")];
      formattedSelectedMonthTotalDebt = applyFilterForValues(
        selectedMonthTotalDebt,
        selectedFigureDivisor,
        selectedDecimalsIn,
      );
    }
    if (data.reportingAccountName === SHORT_TERM_DEBT) {
      sumOf12MonthShortTermLiabilities = Object.values(
        sliceObjectByIndex(data.datas, DATA_INDEX, DATA_LIMIT),
      ).reduce((sum, val) => sum + val, 0);
      formattedSumOf12MonthShortTermLiabilities = applyFilterForValues(
        sumOf12MonthShortTermLiabilities,
        selectedFigureDivisor,
        selectedDecimalsIn,
      );
    }
  });

  return (
    <Card
      style={{
        padding: "6px",
        marginBottom: "20px",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the Short-term Debt to Total Debt Ratio - STDTDR
        (Global View) Report for the selected filters.
      </Typography>
      <>
        {!hrGapResultDataError &&
        hrGapResultData &&
        hrGapResultData?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "10px",
              }}
            >
              {approvalFlow?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendApproverEmailNotify}
                  sx={{
                    fontSize: "0.8rem",
                    boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                    height: "38px",
                    mt: "-10px",
                  }}
                  disabled={
                    selectedUserDetailLoading ||
                    hrFiltersDataLoading ||
                    hrGapResultDataLoading ||
                    !selectedBusinessEntityID ||
                    !selectedModelDatabase ||
                    sendEmailNotifyLoading
                  }
                >
                  Send Reminder Notification
                </Button>
              )}
            </Box>
            <ProgressSteps
              stepsData={approvalFlow}
              isGlobal={true}
              isModelerReport={false}
              isTreasuryKRIReport={true}
              isKRISummaryReport={false}
              isSFCKRIGlobalSummaryReport={false}
              isRegionKRIGlobalSummaryReport={false}
              isTFSCKRIGlobalSummaryReport={false}
              reportDate={selectedMonthEnd}
              loggedInEmail={email}
              handleStepApproval={handleStepApproval}
            />
          </>
        ) : (
          <Typography
            sx={{
              color: "#6a6a6a",
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            Please generate the report to view the Approval Progress
          </Typography>
        )}
      </>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date"}>Month End & Year</InputLabel>
          {selectedUserDetailLoading || hrFiltersDataLoading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={185} height={40} />
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={0}>
                <DatePickerMonthEnds
                  minDate={minDate}
                  maxDate={maxDate}
                  allowedMonthEnds={allowedMonthEnds}
                  value={selectedMonthEnd}
                  onChange={(newValue) => {
                    setSelectedMonthEnd(newValue?.endOf("month"));
                  }}
                  onOpenChange={handleOpenChange}
                  isOpen={isOpen}
                />
              </Box>
              <Box>
                {selectedMonthEnd === null && isTouched && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      fontStyle: "italic",
                    }}
                  >
                    Month End & Year is required
                  </span>
                )}
              </Box>
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading ||
              !selectedBusinessEntityID ||
              !selectedMonthEnd
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.STDTDExportBtn}>
            <STDTDExport
              columns={exportColumns}
              datas={preprocessedDataExport}
              disabled={
                hrGapResultDataLoading || !preprocessedDataExport?.length
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!hrGapResultDataLoading && hrGapResultDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrGapResultDataLoading && !preprocessedData) ||
            (!hrGapResultDataLoading && !preprocessedData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <>
              <ColumnGroupingTable
                columns={columns}
                datas={preprocessedData}
                loading={hrGapResultDataLoading}
                tableHeight={150}
                freezeParentColumnCount={1}
                applyThousandsSeparator={true}
                disablePagination
              />
              {!hrGapResultDataLoading && (
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <Box sx={{ marginBottom: 2 }}>
                    <Table
                      sx={{
                        width: "30%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        borderRadius: "3px",
                        overflow: "hidden",
                        boxShadow: "1px 1px 2px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        backgroundColor: "#EBFFD6",
                      }}
                    >
                      <TableBody>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Short-Term Debt</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {Number(
                              formattedSumOf12MonthShortTermLiabilities,
                            )?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Total Debt</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {Number(
                              formattedSelectedMonthTotalDebt,
                            )?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& td": {
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            <b>Short-Term Debt/Total Debt (%)</b>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid #ccc" }}>
                            {(
                              (formattedSumOf12MonthShortTermLiabilities /
                                formattedSelectedMonthTotalDebt) *
                              100
                            )?.toFixed(2)}
                            %
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default STDTDFiltersAndReports;
